/**
 * Pure helpers for the Devices page — app-version freshness and last-seen
 * connectivity. All derived from real device fields (app_version, last_seen_at)
 * against the tenant's current installer version; nothing here is fabricated.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Split a version like "0.4.1" / "1.2.3-rc2" into numeric segments. */
function parseParts(v: string): number[] {
  return v
    .split(/[.\-+]/)
    .map((x) => parseInt(x, 10))
    .filter((n) => !Number.isNaN(n));
}

/** -1 if a < b, 1 if a > b, 0 if equal (segment-wise numeric compare). */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const pa = parseParts(a);
  const pb = parseParts(b);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

export type AppVersionStatus = 'current' | 'outdated' | 'ahead' | 'unknown';

/**
 * A device's app version relative to the tenant's current installer version.
 * 'unknown' when either side is missing (so the UI stays quiet rather than
 * guessing).
 */
export function appVersionStatus(
  deviceVersion: string | null,
  currentVersion: string | null,
): AppVersionStatus {
  if (!deviceVersion || !currentVersion) return 'unknown';
  const cmp = compareVersions(deviceVersion, currentVersion);
  return cmp === 0 ? 'current' : cmp < 0 ? 'outdated' : 'ahead';
}

/** Compact "last seen" label from an ISO timestamp. Null → "never". */
export function timeAgo(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const secs = Math.floor((now.getTime() - then) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return mins <= 1 ? '1 min ago' : `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor((now.getTime() - then) / DAY_MS);
  if (days < 7) return days === 1 ? 'Yesterday' : `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  return months <= 1 ? '1 month ago' : `${months} months ago`;
}

/** Online = seen within `thresholdMinutes` (default 5). Null → offline. */
export function isOnline(
  iso: string | null,
  now: Date = new Date(),
  thresholdMinutes = 5,
): boolean {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return false;
  return now.getTime() - then <= thresholdMinutes * 60 * 1000;
}
